import { useState, useRef, useEffect } from "react";
import EmojiPicker from "emoji-picker-react";
import AttachmentOptions from "./AttachmentOptions";
import "./style.css";

export default function MessageInput({
  onSendMessage,
  onTypingStart,
  onTypingStop,
  onFileSelect,
  showEmojiPicker,
  onToggleEmojiPicker,
  conversationId,
  onSendMedia,
  currentUser,
}) {
  const [message, setMessage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [showAttachmentOptions, setShowAttachmentOptions] = useState(false);
  const textareaRef = useRef(null);
  const wrapperRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const finalRecordingDurationRef = useRef(0);

  useEffect(() => {
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [message]);

  // Cleanup recording interval on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, []);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      const messageData = {
        type: 'text',
        content: { text: message.trim() }
      };
      
      console.log("Sending message:", messageData);
      onSendMessage(messageData);
      setMessage("");
      onTypingStop?.();
      
      // Close emoji picker after sending message
      if (showEmojiPicker) {
        onToggleEmojiPicker();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessage(value);

    if (value) {
      onTypingStart?.();
    } else {
      onTypingStop?.();
    }
  };

  const handleEmojiClick = (event, emojiObject) => {
    setMessage((prev) => {
      const next = prev + emojiObject.emoji;
      if (next) {
        onTypingStart?.();
      }
      return next;
    });
    textareaRef.current?.focus();
  };

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `voice-message-${Date.now()}.webm`, { type: 'audio/webm' });
        // Only send if recording duration is more than 1 second
        if (finalRecordingDurationRef.current > 1) {
          onSendMedia([audioFile], finalRecordingDurationRef.current);
        }
        // Clean up
        stream.getTracks().forEach(track => track.stop());
        setRecordingDuration(0);
        setRecordingStartTime(null);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingStartTime(Date.now());
      // Start duration timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Capture the final duration before stopping
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      // Calculate the final duration
      let finalDuration = recordingDuration;
      if (recordingStartTime) {
        finalDuration = Math.round((Date.now() - recordingStartTime) / 1000);
      }
      finalRecordingDurationRef.current = finalDuration;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleCancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);
      setRecordingStartTime(null);
      
      // Clear duration timer
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      
      // Stop all tracks
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          // Handle pasted files
          onSendMessage({
            type: file.type.startsWith('image/') ? 'image' : 'file',
            content: { media: [{ file, type: file.type.startsWith('image/') ? 'image' : 'file' }] }
          });
        }
      }
    }
  };

  const handleAttachmentSelect = (files) => {
    // Handle different types of attachments
    files.forEach(file => {
      if (file.type === 'contact') {
        // Handle contact sharing
        onSendMessage({
          type: 'contact',
          content: { contact: file.contact }
        });
      } else if (file.type === 'location') {
        // Handle location sharing
        onSendMessage({
          type: 'location',
          content: { location: file.location }
        });
      } else {
        // Handle regular files
        onSendMedia([file]);
      }
    });
  };

  // Toggle a class on the closest chat window while the emoji picker is open.
  useEffect(() => {
    if (!showEmojiPicker) return;
    
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    
    const chatWindow = wrapper.closest('.chat_window');
    if (!chatWindow) return;

    chatWindow.classList.add('emoji_picker_open');

    return () => {
      if (chatWindow) {
        chatWindow.classList.remove('emoji_picker_open');
      }
    };
  }, [showEmojiPicker]);

  // Close emoji picker on outside click/tap.
  useEffect(() => {
    if (!showEmojiPicker) return;

    const handleClickOutside = (event) => {
      const clickedInsidePicker = emojiPickerRef.current?.contains(event.target);
      const clickedEmojiButton = emojiButtonRef.current?.contains(event.target);
      if (!clickedInsidePicker && !clickedEmojiButton) {
        onToggleEmojiPicker();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showEmojiPicker, onToggleEmojiPicker]);

  return (
    <div className="message_input_wrapper" ref={wrapperRef}>
      {showEmojiPicker && wrapperRef.current && (
        <div className="emoji_picker_container" ref={emojiPickerRef}>
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            height={350}
            width="100%"
            theme="auto"
            disableSkinTonePicker={true}
          />
        </div>
      )}

      <div className="message_input_row">
        <div className="input_actions_left">
          <button
            className="input_action_btn"
            onClick={() => setShowAttachmentOptions(true)}
            title="Attach file"
          >
            <i className="attach_icon">📎</i>
          </button>
          
          <button
            type="button"
            className="messenger_emoji_sender"
            ref={emojiButtonRef}
            onClick={onToggleEmojiPicker}
            title="Add emoji"
            aria-label="Add emoji"
          >
            <span className="messenger_emoji_icon">😊</span>
          </button>
        </div>

        <div className="message_input_field">
          {isRecording ? (
            <div className="recording_input">
              <div className="recording_waveform">
                <div className="waveform_bar"></div>
                <div className="waveform_bar"></div>
                <div className="waveform_bar"></div>
                <div className="waveform_bar"></div>
                <div className="waveform_bar"></div>
                <div className="waveform_bar"></div>
                <div className="waveform_bar"></div>
                <div className="waveform_bar"></div>
              </div>
              <span className="recording_text">
                Recording... {formatDuration(recordingDuration)}
              </span>
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              className="message_textarea"
              placeholder="Type a message..."
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows="1"
              maxLength={2000}
            />
          )}
        </div>

        <div className="input_actions_right">
          {message.trim() ? (
            <button
              className="send_btn"
              onClick={handleSendMessage}
              title="Send message"
            >
              <i className="send_icon">🚀</i>
            </button>
          ) : isRecording ? (
            <div className="recording_actions">
              <button
                className="cancel_recording_btn"
                onClick={handleCancelRecording}
                title="Cancel recording"
              >
                <i className="cancel_icon">✕</i>
              </button>
              <button
                className="stop_recording_btn"
                onClick={handleStopRecording}
                title="Stop recording"
              >
                <i className="stop_icon">⏹</i>
              </button>
            </div>
          ) : (
            <button
              className="voice_btn"
              onMouseDown={handleStartRecording}
              onTouchStart={handleStartRecording}
              onMouseUp={handleStopRecording}
              onMouseLeave={isRecording ? handleStopRecording : undefined}
              onTouchEnd={handleStopRecording}
              title="Hold to record voice message"
            >
              <i className="mic_icon">🎤</i>
            </button>
          )}
        </div>
      </div>

      <AttachmentOptions
        isOpen={showAttachmentOptions}
        onClose={() => setShowAttachmentOptions(false)}
        onFileSelect={handleAttachmentSelect}
        currentUser={currentUser}
      />
    </div>
  );
} 
